import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import {
  LogOut, Inbox, Settings as SettingsIcon, Mail, Phone, MapPin, Clock,
  Trash2, CheckCircle2, Archive, ArrowUpRight, Save, User as UserIcon,
  Search
} from "lucide-react";

const TABS = [
  { key: "enquiries", label: "Enquiries", icon: Inbox },
  { key: "settings",  label: "Business info", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [stats, setStats] = useState(null);
  const [username, setUsername] = useState(localStorage.getItem("rs_admin_user") || "admin");

  useEffect(() => {
    const t = localStorage.getItem("rs_admin_token");
    if (!t) { nav("/admin", { replace: true }); return; }
    api.adminStats().then(setStats).catch(() => {
      localStorage.removeItem("rs_admin_token");
      nav("/admin", { replace: true });
    });
  }, [nav]);

  const logout = () => {
    localStorage.removeItem("rs_admin_token");
    localStorage.removeItem("rs_admin_user");
    nav("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-bone text-ink">
      {/* Top bar */}
      <header className="bg-ink text-bone border-b border-line-light">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="font-display-tight text-2xl">RS<span className="text-amber italic">.</span></span>
            <span className="text-eyebrow text-bone/60 hidden md:inline">Admin Console</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" target="_blank" rel="noreferrer" className="hidden md:inline-flex items-center gap-1.5 text-eyebrow text-bone/70 hover:text-amber">View site <ArrowUpRight className="w-3.5 h-3.5" /></Link>
            <div className="hidden md:flex items-center gap-2 text-sm text-bone/70"><UserIcon className="w-4 h-4" />{username}</div>
            <button onClick={logout} className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase border border-line-light px-3 py-2 hover:bg-amber hover:text-ink hover:border-amber transition-colors">
              Logout <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-10">
        {/* Stats cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { l: "Total Enquiries", v: stats?.total ?? "—" },
            { l: "New", v: stats?.new ?? "—", accent: true },
            { l: "Read", v: stats?.read ?? "—" },
            { l: "Archived", v: stats?.archived ?? "—" },
          ].map((s, i) => (
            <div key={i} className={`admin-card p-6 ${s.accent ? "border-amber" : ""}`}>
              <div className="text-eyebrow text-ink/50 mb-2">{s.l}</div>
              <div className="font-display-tight text-5xl text-ink">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-8 border-b border-line">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-5 py-3 text-[12px] tracking-[0.22em] uppercase font-medium border-b-2 transition-colors ${
                  active ? "border-amber text-ink" : "border-transparent text-ink/50 hover:text-ink"
                }`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "enquiries" && <EnquiriesPanel onChanged={() => api.adminStats().then(setStats)} />}
        {tab === "settings"  && <SettingsPanel />}
      </div>
    </div>
  );
}

function EnquiriesPanel({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.adminListEnquiries().then((r) => { setRows(r); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.adminUpdateEnquiry(id, status);
    load(); onChanged?.();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    await api.adminDeleteEnquiry(id);
    load(); onChanged?.();
    if (selected?.id === id) setSelected(null);
  };

  const visible = rows.filter((r) => {
    if (filter !== "all" && r.status !== filter) return false;
    if (q && !`${r.name} ${r.email} ${r.message} ${r.service} ${r.postcode}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      {/* List */}
      <div className="lg:col-span-7">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, message…"
              className="w-full border border-line pl-10 pr-3 py-3 text-sm focus:border-amber focus:outline-none bg-white" />
          </div>
          {["all", "new", "read", "archived"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-2 text-[11px] tracking-[0.22em] uppercase border ${
                filter === f ? "bg-ink text-bone border-ink" : "border-line text-ink/70 hover:border-ink"
              }`}>{f}</button>
          ))}
        </div>

        <div className="admin-card">
          {loading ? (
            <div className="p-10 text-center text-ink-soft">Loading enquiries…</div>
          ) : visible.length === 0 ? (
            <div className="p-16 text-center text-ink-soft">
              <Inbox className="w-8 h-8 mx-auto mb-3 text-ink/30" />
              No enquiries match your filters.
            </div>
          ) : (
            <ul className="divide-y divide-line">
              {visible.map((r) => {
                const active = selected?.id === r.id;
                return (
                  <li key={r.id}>
                    <button onClick={() => { setSelected(r); if (r.status === "new") setStatus(r.id, "read"); }}
                      className={`w-full text-left px-5 py-4 flex items-start gap-4 hover:bg-bone-2 transition-colors ${active ? "bg-bone-2" : ""}`}>
                      <div className={`h-2 w-2 rounded-full mt-2 ${r.status === "new" ? "bg-amber" : r.status === "archived" ? "bg-ink/20" : "bg-emerald-500/70"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-4">
                          <div className="font-display text-lg text-ink truncate">{r.name}</div>
                          <div className="text-xs text-ink/50 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</div>
                        </div>
                        <div className="text-sm text-ink/60 truncate">{r.service || "General"} · {r.email}</div>
                        <div className="text-sm text-ink/75 truncate mt-1">{r.message}</div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Detail */}
      <div className="lg:col-span-5">
        {selected ? (
          <div className="admin-card p-6 sticky top-6">
            <div className="flex items-center justify-between mb-4">
              <span className={`text-[11px] tracking-[0.22em] uppercase px-2.5 py-1 ${
                selected.status === "new" ? "bg-amber text-ink" : selected.status === "archived" ? "bg-ink/10 text-ink/70" : "bg-emerald-500/15 text-emerald-700"
              }`}>{selected.status}</span>
              <div className="text-xs text-ink/50">{new Date(selected.created_at).toLocaleString()}</div>
            </div>
            <h3 className="font-display text-3xl text-ink">{selected.name}</h3>
            <div className="grid grid-cols-2 gap-3 mt-5 mb-5">
              <a href={`mailto:${selected.email}`} className="border border-line px-3 py-3 hover:border-amber text-sm flex items-center gap-2"><Mail className="w-4 h-4 text-amber" /><span className="truncate">{selected.email}</span></a>
              <a href={`tel:${selected.phone}`} className="border border-line px-3 py-3 hover:border-amber text-sm flex items-center gap-2"><Phone className="w-4 h-4 text-amber" /><span>{selected.phone || "—"}</span></a>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="border border-line px-3 py-3 text-sm"><div className="text-eyebrow text-ink/45 mb-1">Postcode</div>{selected.postcode || "—"}</div>
              <div className="border border-line px-3 py-3 text-sm"><div className="text-eyebrow text-ink/45 mb-1">Service</div>{selected.service || "General"}</div>
            </div>
            <div className="border border-line p-4 mb-5 bg-bone-2/40">
              <div className="text-eyebrow text-amber mb-2">Message</div>
              <p className="text-ink leading-relaxed whitespace-pre-wrap">{selected.message}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setStatus(selected.id, "read")} className="flex items-center gap-2 border border-line px-3 py-2 text-[11px] tracking-[0.22em] uppercase hover:bg-ink hover:text-bone transition-colors"><CheckCircle2 className="w-4 h-4" /> Mark read</button>
              <button onClick={() => setStatus(selected.id, "archived")} className="flex items-center gap-2 border border-line px-3 py-2 text-[11px] tracking-[0.22em] uppercase hover:bg-ink hover:text-bone transition-colors"><Archive className="w-4 h-4" /> Archive</button>
              <button onClick={() => remove(selected.id)} className="flex items-center gap-2 border border-red-300 text-red-600 px-3 py-2 text-[11px] tracking-[0.22em] uppercase hover:bg-red-600 hover:text-bone transition-colors"><Trash2 className="w-4 h-4" /> Delete</button>
              <a href={`mailto:${selected.email}?subject=Re:%20Your%20RS%20Construction%20enquiry`} className="ml-auto flex items-center gap-2 bg-amber text-ink px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-bold hover:bg-ink hover:text-bone transition-colors">Reply <ArrowUpRight className="w-4 h-4" /></a>
            </div>
          </div>
        ) : (
          <div className="admin-card p-10 text-center text-ink-soft">
            Select an enquiry to view details.
          </div>
        )}
      </div>
    </div>
  );
}

function SettingsPanel() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { api.getSettings().then(setForm); }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true); setSaved(false);
    try { await api.adminUpdateSettings(form); setSaved(true); } finally { setSaving(false); }
    setTimeout(() => setSaved(false), 2500);
  };

  if (!form) return <div className="p-10 text-center text-ink-soft">Loading…</div>;

  const Field = ({ label, k, icon: Icon }) => (
    <label className="block">
      <span className="text-eyebrow text-ink/50 mb-2 block flex items-center gap-2">{Icon && <Icon className="w-3.5 h-3.5 text-amber" />} {label}</span>
      <input value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)}
        className="w-full border border-line px-3 py-3 focus:border-amber focus:outline-none bg-white text-sm" />
    </label>
  );

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 admin-card p-8 space-y-8">
        <section>
          <h3 className="font-display text-2xl text-ink mb-5">Identity</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Company name" k="company_name" />
            <Field label="Legal name" k="full_name" />
            <div className="md:col-span-2"><Field label="Tagline" k="tagline" /></div>
          </div>
        </section>
        <section>
          <h3 className="font-display text-2xl text-ink mb-5">Contact</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Primary phone" k="phone" icon={Phone} />
            <Field label="Secondary phone" k="phone_alt" icon={Phone} />
            <Field label="Mobile" k="mobile" icon={Phone} />
            <Field label="Email" k="email" icon={Mail} />
          </div>
        </section>
        <section>
          <h3 className="font-display text-2xl text-ink mb-5">Studio address</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Line 1" k="address_line1" icon={MapPin} />
            <Field label="Line 2" k="address_line2" icon={MapPin} />
            <Field label="Line 3" k="address_line3" icon={MapPin} />
            <Field label="City / Postcode" k="address_city" icon={MapPin} />
          </div>
        </section>
        <section>
          <h3 className="font-display text-2xl text-ink mb-5">Opening hours</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Weekdays" k="hours_weekdays" icon={Clock} />
            <Field label="Saturday" k="hours_saturday" icon={Clock} />
            <Field label="Sunday" k="hours_sunday" icon={Clock} />
          </div>
        </section>
        <section>
          <h3 className="font-display text-2xl text-ink mb-5">Social</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Instagram" k="instagram" />
            <Field label="Facebook" k="facebook" />
            <Field label="Google reviews" k="google" />
          </div>
        </section>

        <div className="flex items-center gap-4">
          <button onClick={save} disabled={saving}
            className="flex items-center gap-2 bg-ink text-bone px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-bold hover:bg-amber hover:text-ink transition-colors disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
          </button>
          {saved && <span className="text-sm text-emerald-700">Saved — live on site.</span>}
        </div>
      </div>

      <aside className="lg:col-span-4">
        <div className="admin-card p-6 sticky top-6">
          <div className="text-eyebrow text-amber mb-3">— Where this shows</div>
          <ul className="space-y-3 text-sm text-ink-soft">
            <li>Header top-strip (phone/email)</li>
            <li>Footer contact block</li>
            <li>Contact page studio info</li>
            <li>Map & office hours</li>
          </ul>
          <p className="mt-6 text-xs text-ink-mute">Changes are live immediately — refresh any public page to verify.</p>
        </div>
      </aside>
    </div>
  );
}
